import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, validateSession, sessionProfile, invalidSession } from '@/lib/api';

const target = kind => kind === 'admin' ? '/admin/dashboard' : '/user/dashboard';
export default function SessionEntry({ children }) {
  const location = useLocation(), navigate = useNavigate();
  const [ready, setReady] = useState(null), [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const path = location.pathname;
  const exempt = path === '/user/password-required' || path === '/auth/continue' || (path === '/users' && new URLSearchParams(location.search).get('switch') === '1');
  const requested = path.startsWith('/admin') ? 'admin' : path.startsWith('/user') ? 'user' : null;
  const remembered = auth.remembered(), last = remembered.accounts[remembered.last];
  const kind = requested || last?.kind || (auth.getUser() ? 'user' : auth.getAdmin() ? 'admin' : null);
  const token = !requested && last ? last.token : kind === 'admin' ? auth.getAdmin() : auth.getUser();
  useEffect(() => {
    const revalidate = () => { if (document.visibilityState === 'visible') setRevision(value => value + 1); };
    const invalid = event => {
      if (event.detail.token === auth.getAdmin()) { auth.forget('admin', event.detail.token); auth.clearAdmin(); }
      if (event.detail.token === auth.getUser()) { auth.forget('user', event.detail.token); auth.clearUser(); }
      setReady(null); setRevision(value => value + 1);
    };
    const storage = () => { setReady(null); setRevision(value => value + 1); };
    const timer = window.setInterval(revalidate, 60000);
    window.addEventListener('pageshow', revalidate);
    window.addEventListener('storage', storage);
    window.addEventListener('mb:session-invalid', invalid);
    document.addEventListener('visibilitychange', revalidate);
    return () => { clearInterval(timer); window.removeEventListener('pageshow', revalidate); window.removeEventListener('storage', storage); window.removeEventListener('mb:session-invalid', invalid); document.removeEventListener('visibilitychange', revalidate); };
  }, []);
  useEffect(() => {
    let live = true;
    setError(false);
    if (exempt) return;
    const restore = async () => {
      const candidates = token ? [{ kind, token }] : [];
      if (token && !requested) candidates.push(...Object.values(auth.remembered().accounts).filter(s => s.token !== token).sort((a,b) => b.used-a.used));
      for (const saved of candidates) {
        try {
          const data = await validateSession(saved.kind, saved.token);
          if (!live) return;
          saved.kind === 'admin' ? auth.setAdmin(data.token) : auth.setUser(data.token);
          setReady(data.token);
          window.dispatchEvent(new CustomEvent('mb:session-validated', { detail: { kind: saved.kind, profile: data.profile } }));
          if (['/', '/users', '/admin'].includes(path)) navigate(target(saved.kind), { replace: true });
          return;
        } catch (failure) {
          if (!live) return;
          if (failure?.response?.status === 428) { navigate('/user/password-required', { replace: true }); return; }
          if (!invalidSession(failure)) { setError(true); return; }
          auth.forget(saved.kind, saved.token);
          saved.kind === 'admin' ? auth.clearAdmin() : auth.clearUser();
          setReady(null);
          if (saved.kind === 'admin' && auth.getUser()) { navigate('/user/dashboard', { replace: true }); return; }
        }
      }
      if (live) {
        if (path.startsWith('/admin/dashboard') || path.startsWith('/admin/view-user/')) navigate('/admin', { replace: true });
        else if (path.startsWith('/user/dashboard')) navigate('/users', { replace: true });
        setReady(null);
      }
    };
    restore();
    return () => { live = false; };
  }, [path, location.search, navigate, revision, exempt, kind, token, requested]);
  // Keep the mounted router and page data during internal navigation and
  // background refreshes. Only a new identity requires a blocking first check.
  if (!exempt && token && ready !== token && !sessionProfile(token)) return <main dir="rtl" className="min-h-screen grid place-items-center"><div>{error ? 'تعذر الاتصال للتحقق من الجلسة. حسابك محفوظ.' : 'جارٍ التحقق من الجلسة…'}{error && <button onClick={() => setRevision(value => value + 1)}>إعادة المحاولة</button>}</div></main>;
  return children;
}
